import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAIFillerStore, useAIFillerActions } from '../stores/aiFillerStore';
import aiFillerService from '../services/aiFillerService';
import notify from '../lib/notify';
import { FormField, FieldValue } from '../types';

// Maximum text length to avoid token-limit errors. ~4 chars per token, leaving
// room for the prompt template on top.
const MAX_TEXT_LENGTH = 12000;

/**
 * Hook for using GenieFiller (form filling) functionality.
 */
export const useAIFiller = () => {
  const { t, i18n } = useTranslation();
  const store = useAIFillerStore();
  const actions = useAIFillerActions();
  const [localLoading, setLocalLoading] = useState(false);

  const triggerFiller = useCallback(
    (fields: FormField[], context?: string) => {
      actions.showFiller(fields, context);
    },
    [actions],
  );

  const processText = useCallback(
    async (text: string): Promise<FieldValue[]> => {
      if (!text.trim()) {
        actions.setError(t('aiFiller.enterTextToAnalyze'));
        return [];
      }

      if (store.currentFields.length === 0) {
        actions.setError(t('aiFiller.noFormFieldsAvailable'));
        return [];
      }

      let processedText = text.trim();
      if (processedText.length > MAX_TEXT_LENGTH) {
        processedText = processedText.substring(0, MAX_TEXT_LENGTH);
        notify.warning(
          t('aiFiller.textTruncatedTo', { max: MAX_TEXT_LENGTH.toLocaleString(i18n.language) }),
        );
      }

      setLocalLoading(true);
      actions.setProcessing(true);
      actions.setError(null);

      try {
        const response = await aiFillerService.fillForm({
          user_text: processedText,
          fields: store.currentFields,
          form_context: store.formContext,
        });

        if (response.success) {
          actions.setFilledValues(response.filled_fields);
          actions.setSuggestions(response.suggestions || []);

          if (response.filled_fields.length > 0) {
            notify.success(t('aiFiller.fieldsFilledSuccess', { count: response.filled_fields.length }));
          } else {
            notify.warning(t('aiFiller.noInfoExtracted'));
          }

          return response.filled_fields;
        } else {
          const errorMsg = response.message || t('aiFiller.analyzeTextError');
          actions.setError(errorMsg);
          notify.error(errorMsg);
          return [];
        }
      } catch (error) {
        const err = error as { response?: { data?: { detail?: string | Record<string, unknown> }; status?: number }; message?: string };
        const detail = err?.response?.data?.detail;
        const isQuotaExceeded =
          err?.response?.status === 403 &&
          typeof detail === 'object' &&
          detail !== null &&
          (detail as Record<string, unknown>)['code'] === 'org_quota_exceeded';
        if (isQuotaExceeded) {
          const msg = t('genieArtifact.quotaBlocked.message');
          actions.setError(msg);
          notify.error(t('genieArtifact.quotaBlocked.title'));
          return [];
        }
        const errorMsg = (typeof detail === 'string' ? detail : null) || err?.message || t('aiFiller.analyzeError');
        actions.setError(errorMsg);
        notify.error(errorMsg);
        return [];
      } finally {
        setLocalLoading(false);
        actions.setProcessing(false);
      }
    },
    [store.currentFields, store.formContext, actions, t, i18n.language],
  );

  const applyValues = useCallback(
    (onApply: (values: FieldValue[]) => void) => {
      if (store.filledValues.length > 0) {
        onApply(store.filledValues);
        notify.success(t('aiFiller.valuesApplied'));
        actions.closePanel();
      }
    },
    [store.filledValues, actions, t],
  );

  const dismiss = useCallback(() => {
    actions.hideFiller();
  }, [actions]);

  return {
    // State
    isOpen: store.isOpen,
    isVisible: store.isVisible,
    isProcessing: store.isProcessing || localLoading,
    userText: store.userText,
    filledValues: store.filledValues,
    error: store.error,
    suggestions: store.suggestions,
    currentFields: store.currentFields,

    // Actions
    triggerFiller,
    processText,
    applyValues,
    openPanel: actions.openPanel,
    closePanel: actions.closePanel,
    setUserText: actions.setUserText,
    dismiss,
  };
};
